import { useState, useEffect, useCallback } from 'react'
import Layout from '../components/Layout.jsx'
import BowtieVisual from '../components/BowtieVisual.jsx'
import KpiDrawer from '../components/KpiDrawer.jsx'
import Icon from '../components/icons.jsx'
import SyncStatusBanner from '../components/SyncStatusBanner.jsx'
import { MetricFunnel } from '../components/Metrics.jsx'
import DailyTrendChart from '../components/DailyTrendChart.jsx'
import PageState from '../components/PageState.jsx'
import PageFooter from '../components/PageFooter.jsx'

// import DRECharts from '../components/dre/DRECharts.jsx' // oculto por enquanto, reutilizar futuramente
import DRETable from '../components/dre/DRETable.jsx'

import { useViewMode } from '../hooks/useViewMode.js'
import { useCampaignFilter, setAvailableCampaigns } from '../hooks/useCampaignFilter.js'
import { useApp } from '../context/AppContext.jsx'
import { useDashboard } from '../hooks/useDashboard.js'
import { useDRE } from '../hooks/useDRE.js'

import { getCampaigns } from '../db/api.js'

function KpiCardCompact({ kpi, onClick }) {
  return (
    <button
      className="kpi-card-compact"
      type="button"
      title={kpi.hint}
      onClick={onClick}
      aria-label={`${kpi.label}: ${kpi.value}`}
    >
      <div className="kpi-compact-head">
        <span className="kpi-compact-label">{kpi.label}</span>
      </div>

      <div className="kpi-compact-value">{kpi.value}</div>
    </button>
  )
}

function DashboardEmpty({ onClear }) {
  return (
    <div className="dash-empty">
      <div className="dash-empty-icon">
        <Icon n="filter" s={20} />
      </div>

      <h2 className="dash-empty-title">
        Nenhuma campanha selecionada
      </h2>

      <p className="dash-empty-desc">
        Selecione ao menos uma campanha no filtro do topo para visualizar os dados da dashboard.
      </p>

      <button
        className="dash-empty-cta"
        onClick={onClear}
        type="button"
      >
        Ver todas as campanhas
      </button>
    </div>
  )
}

// Métricas selecionáveis no gráfico diário da Dashboard (Google Ads + funil).
const DASH_CHART_METRICS = [
  { key: 'investment', label: 'Investimento', fmt: 'currency', color: '#1d4ed8' },
  { key: 'impressions', label: 'Impressões', fmt: 'number', color: '#8b5cf6' },
  { key: 'clicks', label: 'Cliques', fmt: 'number', color: '#0ea5e9' },
  { key: 'conversions', label: 'Conversões', fmt: 'number', color: '#10b981' },
  { key: 'visits', label: 'Visitas', fmt: 'number', color: '#f59e0b' },
  { key: 'leads', label: 'Leads', fmt: 'number', color: '#ef4444' },
]

function DRESection({ data, loading, error, granularity, onGranularity, funnelTotals, series, onRetry }) {
  if (loading || error) {
    return <PageState loading={loading} error={error} loadingText="Carregando DRE..." onRetry={onRetry} />
  }

  if (!data) return null

  return (
    <section className="dre-dashboard-section">
      <div className="dre-dashboard-header">
        <h2 className="dre-dashboard-title">
          Dashboard
        </h2>
      </div>

      {/* 1. Cards superiores — funil (padrão visual do Funil Orgânico do SEO) */}
      <MetricFunnel
        rows={[
          [
            { label: 'Impressões', value: funnelTotals.impressions, format: 'number' },
            { label: 'CTR (%)', value: funnelTotals.ctr, format: 'percent' },
            {
              label: 'Cliques', value: funnelTotals.clicks, format: 'number',
              sub: { label: 'CPC', value: funnelTotals.cpc, format: 'currency' },
            },
            { label: 'Taxa de cliques p/ visitantes', value: funnelTotals.taxaCliquesVisitantes, format: 'percent' },
            {
              label: 'Visitantes', value: funnelTotals.visits, format: 'number',
              sub: { label: 'Custo por visita', value: funnelTotals.custoPorVisita, format: 'currency' },
            },
            { label: 'Taxa de visitas p/ leads', value: funnelTotals.taxaVisitasLeads, format: 'percent' },
            {
              label: 'Leads', value: funnelTotals.leads, format: 'number',
              sub: { label: 'CPL', value: funnelTotals.cpl, format: 'currency' },
            },
            { label: 'Taxa de lead p/ lead qualificado', value: funnelTotals.taxaLeadQualificado, format: 'percent' },
            { label: 'Leads Qualificados', value: funnelTotals.qualified, format: 'number' },
            { label: 'Leads para vendas', value: funnelTotals.taxaLeadsVendas, format: 'percent' },
          ],
        ]}
      />

      {/* 1b. Segundo funil — resultado financeiro */}
      <MetricFunnel
        rows={[
          [
            { label: 'Investimento Total', value: funnelTotals.cost, format: 'currency' },
            { label: 'ROAS', value: funnelTotals.roas, format: 'multiplier' },
            { label: 'Valor Total de Vendas', value: funnelTotals.revenue, format: 'currency' },
            { label: 'Ticket Médio', value: funnelTotals.ticketMedio, format: 'currency' },
          ],
        ]}
      />

      {/* 1c. Gráfico de série diária — entre os cards de métricas e o DRE.
          Sempre exibido, mesmo sem investimento/métricas no período. */}
      <DailyTrendChart series={series || []} metrics={DASH_CHART_METRICS} />

      {/* 2. Tabela */}
      <DRETable
        months={data.months}
        blocks={data.blocks}
        granularity={granularity}
        onGranularity={onGranularity}
      />

      {/* 3. Gráficos — ocultos por enquanto, reutilizar futuramente.
      <DRECharts charts={data.charts} />
      */}
    </section>
  )
}

export default function Dashboard() {
  const { isAdvanced } = useViewMode()

  // Período global (PeriodPicker da topbar) — mesmo calendário das demais páginas.
  const { selectedClient, period } = useApp()

  const {
    selectedIds,
    count,
    total,
    isFiltered,
    isAllSelected,
    selectAll,
  } = useCampaignFilter()

  const [activeKpi, setActiveKpi] = useState(null)

  const [campaignsLoading, setCampaignsLoading] = useState(true)
  const [campaignsError, setCampaignsError] = useState(null)

  // Bump para forçar reload das campanhas quando a coleta inicial conclui
  // (ver handleSyncComplete + dep do useEffect de campanhas).
  const [reloadKey, setReloadKey] = useState(0)

  // DRE
  const currentYear = new Date().getFullYear()

  const [dreYear] = useState(
    Math.max(currentYear, 2026)
  )

  const [dreGranularity, setDreGranularity] = useState('month')

  // ─────────────────────────────────────────────────────────────
  // Campanhas
  // ─────────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false

    async function load() {
      setCampaignsLoading(true)
      setCampaignsError(null)

      try {
        const campaigns = await getCampaigns()

        if (!cancelled) {
          const normalized = (campaigns || []).map((c) => ({
            id: c.id || c.google_campaign_id || String(c.campaign_id),

            name:
              c.name ||
              c.campaign_name ||
              'Campanha',

            source: c.channel?.includes('SEARCH')
              ? 'Google Ads'
              : 'Meta Ads',

            status:
              (c.status || 'ENABLED').toLowerCase() === 'enabled'
                ? 'active'
                : 'paused',
          }))

          setAvailableCampaigns(normalized)
        }
      } catch (err) {
        if (!cancelled) {
          console.error(
            '[Dashboard] erro ao carregar campanhas:',
            err
          )

          setCampaignsError(err.message)
        }
      } finally {
        if (!cancelled) setCampaignsLoading(false)
      }
    }

    load()

    return () => {
      cancelled = true
    }
  }, [selectedClient?.id, reloadKey])

  // ─────────────────────────────────────────────────────────────
  // Dashboard
  // ─────────────────────────────────────────────────────────────
  const dashboardCampaignIds = isAllSelected
    ? null
    : selectedIds

  const {
    data,
    loading: dataLoading,
    error: dataError,
    refetch: refetchDashboard,
  } = useDashboard(
    period.start,
    period.end,
    dashboardCampaignIds,
    selectedClient?.id
  )

  // Coleta inicial concluída → recarrega KPIs/cards e a lista de campanhas
  // (que nascem do mesmo run). O banner chama isso via useSyncStatus.
  const handleSyncComplete = useCallback(() => {
    refetchDashboard()
    setReloadKey((k) => k + 1)
  }, [refetchDashboard])

  // ─────────────────────────────────────────────────────────────
  // DRE
  // ─────────────────────────────────────────────────────────────
  const {
    data: dreData,
    loading: dreLoading,
    error: dreError,
    refetch: refetchDre,
  } = useDRE(
    dreYear,
    selectedClient?.id,
    dreGranularity
  )

  // ─────────────────────────────────────────────────────────────
  // Estados gerais
  // ─────────────────────────────────────────────────────────────
  const loading = campaignsLoading || dataLoading

  const error = campaignsError || dataError

  const kpisBase = data
    ? [
        data.kpis.investment,
        data.kpis.revenue,
        data.kpis.roas,
        data.kpis.cac,
      ].map((k, i) => ({
        ...k,
        key: ['investment', 'revenue', 'roas', 'cac'][i],
      }))
    : []

  // Cards superiores — padrão visual do funil orgânico (single line, sub-indicadores).
  const m = data?.metrics || {}
  const funnelTotals = {
    impressions: m.impressions || 0,
    clicks: m.clicks || 0,
    visits: m.visits || 0,
    leads: m.leads || 0,
    qualified: m.qualified || 0,
    sales: m.sales || 0,
    cost: m.investment || 0,
    revenue: m.revenue || 0,
    cpc: m.clicks > 0 ? m.investment / m.clicks : 0,
    cpl: m.leads > 0 ? m.investment / m.leads : 0,
    cac: m.sales > 0 ? m.investment / m.sales : 0,
    roi: m.investment > 0 ? ((m.revenue - m.investment) / m.investment) * 100 : 0,
    roas: m.investment > 0 ? m.revenue / m.investment : 0,
    ticketMedio: m.sales > 0 ? m.revenue / m.sales : 0,
    custoPorVisita: m.visits > 0 ? m.investment / m.visits : 0,
    ctr: m.impressions > 0 ? (m.clicks / m.impressions) * 100 : 0,
    taxaCliquesVisitantes: m.clicks > 0 ? (m.visits / m.clicks) * 100 : 0,
    taxaVisitasLeads: m.visits > 0 ? (m.leads / m.visits) * 100 : 0,
    taxaLeadQualificado: m.leads > 0 ? (m.qualified / m.leads) * 100 : 0,
    taxaLeadsVendas: m.leads > 0 ? (m.sales / m.leads) * 100 : 0,
  }

  const BLOCKED_DRAWER_KEYS = new Set([
    'investment',
    'revenue',
    'roas',
    'cac',
  ])

  const onKpiClick = (kpiKey, kpi) => {
    if (BLOCKED_DRAWER_KEYS.has(kpiKey)) return

    setActiveKpi({
      key: kpiKey,
      kpi,
    })
  }

  const onCloseDrawer = () => setActiveKpi(null)

  const isEmpty = count === 0 && total > 0

  return (
    <Layout
      breadcrumb={[{ label: 'Marketing' }, { label: 'Google Ads', to: '/google-ads' }, { label: 'Dashboard' }]}
      title="Google Ads — Dashboard"
      showPeriod
    >
      <div className="dash-shell">

        {/* Status da coleta inicial (fila sync_jobs). Faz polling sozinho e
            recarrega os dados quando termina. */}
        <SyncStatusBanner onComplete={handleSyncComplete} />

        {/* Banner filtro */}
        {isFiltered && !isEmpty && !loading && (
          <div className="dash-filter-banner">
            <Icon n="filter" s={12} />

            <span>
              Mostrando dados de{' '}
              <strong>
                {count} de {total} campanhas
              </strong>
            </span>

            <button
              className="dash-filter-clear"
              onClick={selectAll}
              type="button"
            >
              Ver todas
            </button>
          </div>
        )}

        <PageState
          loading={loading}
          error={error}
          loadingText="Carregando dados do dashboard..."
          onRetry={refetchDashboard}
        />

        {/* Empty */}
        {!loading && !error && isEmpty && (
          <DashboardEmpty onClear={selectAll} />
        )}

        {/* Conteúdo */}
        {!loading && !error && !isEmpty && data && (
          <>
            {/* KPIs técnicos */}
            {isAdvanced && (
              <div className="kpi-secondary">
                <span className="kpi-secondary-label">
                  Métricas técnicas
                </span>

                <div className="kpi-grid-compact">
                  {data.kpisAdvanced.map((k) => (
                    <KpiCardCompact
                      key={k.key}
                      kpi={k}
                      onClick={() => onKpiClick(k.key, k)}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Funil 
            <BowtieVisual data={data.funnel} />
            */}

            {/* DRE */}
            <DRESection
              data={dreData}
              loading={dreLoading}
              error={dreError}
              granularity={dreGranularity}
              onGranularity={setDreGranularity}
              funnelTotals={funnelTotals}
              series={data.series}
              onRetry={refetchDre}
            />

            {/* Footer */}
            <PageFooter text={`Dados atualizados ${data.freshness.label}`} onRefresh={refetchDashboard} />
          </>
        )}
      </div>

      <KpiDrawer
        open={!!activeKpi}
        kpiKey={activeKpi?.key}
        kpi={activeKpi?.kpi}
        series={data?.series}
        onClose={onCloseDrawer}
      />
    </Layout>
  )
}